import { chain, isArray } from "lodash"
import { generateGathererKeys } from "connected-components/dashboard/helpers/query-key"
import { buildContributorKey } from "helpers/contributors"
import { getFactorSensitivityQueryParams } from "helpers/factorSensitivity"
import { getContributorLabel, NOT_HELD } from "../../connected-components/asset-view/helpers"
import { getGroupByQueryParameters, getGroupByFields, sanitizePositionSet, getGroupBySubqueryParameters } from "../helpers"
import { getFactorValueByMetricDisplay } from "./helpers/factor-utils"

const FACTOR_SENSITIVITY_FIELDS = `
  id
  name
  category
  netContribution: sensitivity
`

const FACTOR_SENSITIVITY_SUBQUERY = `
factors(category: $category) {
  ${FACTOR_SENSITIVITY_FIELDS}
  standalone: securityFactorSensitivity
}`

const GROUPED_FACTOR_SENSITIVITY_SUBQUERY = `
id
name
factors(category: $category) {
  ${FACTOR_SENSITIVITY_FIELDS}
}
contributors {
  id
  assetClass
  modelProviderId
  notHeld
  factors(category: $category) {
    ${FACTOR_SENSITIVITY_FIELDS}
    standalone: securityFactorSensitivity
  }
}`

function getUngroupedFactorSensitivitySubQuery() {
  const extraParameters = ", includeNotHeld: $includeNotHeld"
  return `
    factorSensitivity(type: $factorSensitivityType horizon: $horizon stdDev: $stdDev days: $days) {
      factors {
        ${FACTOR_SENSITIVITY_FIELDS}
      }
    }
    factorSensitivityContributors(type: $factorSensitivityType horizon: $horizon stdDev: $stdDev days: $days ${extraParameters}) {
      id
      assetClass
      modelProviderId
      ${FACTOR_SENSITIVITY_SUBQUERY}
    }
  `
}

function getGroupedFactorSensitivitySubQuery(groupByFields) {
  const extraParameters = ", includeNotHeld: $includeNotHeld"
  return `
    factorSensitivity(type: $factorSensitivityType horizon: $horizon stdDev: $stdDev days: $days) {
      factors {
        ${FACTOR_SENSITIVITY_FIELDS}
      }
    }
    groupedFactorSensitivityContributors(type: $factorSensitivityType horizon: $horizon stdDev: $stdDev days: $days ${extraParameters} ${getGroupBySubqueryParameters(groupByFields)}) {
      ${GROUPED_FACTOR_SENSITIVITY_SUBQUERY}
    }
  `
}

function getQuery(groupByFields, shouldUseIntradayQuery) {
  const extraParameters = groupByFields ? getGroupByQueryParameters(groupByFields) : ""
  const innerQuery = groupByFields
    ? getGroupedFactorSensitivitySubQuery(groupByFields)
    : getUngroupedFactorSensitivitySubQuery()

  if (shouldUseIntradayQuery) {
    return `
    query FactorSensitivityContributorsQuery($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $on: Date!, $includeNotHeld: Boolean, $batchId: Id $factorSensitivityType: FactorSensitivityType!, $days: PositiveInt, $stdDev: PositiveFloat, $horizon: FactorSensitivityTimeHorizon!, $category: [String] ${extraParameters}) {
      model(id: $modelId) {
        simulation:intradaySimulationBatch(positionSet: $positionSet, on: $on, batchId: $batchId, base: $base) {
          ${innerQuery}
        }
      }
    }`
  }

  return `
  query FactorSensitivityContributorsQuery($modelId: String!, $positionSet: PositionSetInput!, $date: Date!, $base: [PositionSetInput], $includeNotHeld: Boolean $factorSensitivityType: FactorSensitivityType!, $days: PositiveInt, $stdDev: PositiveFloat, $horizon: FactorSensitivityTimeHorizon!, $category: [String] ${extraParameters}) {
    model(id: $modelId) {
      simulation(
        positionSet: $positionSet
        from: $date
        to: $date
        base: $base
      ) {
        ${innerQuery}
      }
    }
  }`
}

function getFactorSensitivityValues(factors, selectedMetricDisplay) {
  if (!factors) {
    return {}
  }

  return factors.reduce((acc, factor) => {
    if (selectedMetricDisplay) {
      acc[factor.id] = getFactorValueByMetricDisplay(factor, selectedMetricDisplay)
    } else {
      acc[factor.id] = { netContribution: factor.netContribution, standalone: factor.standalone }
    }
    return acc
  }, {})
}

export function parseContributors(prefix, contributors, options = {}) {
  if (!isArray(contributors)) {
    return {}
  }

  const selectedMetricDisplay = options.selectedMetricDisplay

  return contributors.reduce((acc, contributor) => {
    if (!contributor) {
      return acc
    }

    if (contributor.notHeld && options.notHeldRollup) {
      if (!acc[NOT_HELD]) {
        acc[NOT_HELD] = {
          label: "Not Held",
          notHeld: true,
          values: {
            [prefix]: {},
          },
        }
      }

      const contributorData = getFactorSensitivityValues(contributor.factors, selectedMetricDisplay)
      Object.entries(contributorData).forEach(([key, value]) => {
        if (typeof value === "number") {
          if (!acc[NOT_HELD].values[prefix][key]) {
            acc[NOT_HELD].values[prefix][key] = value
          } else {
            acc[NOT_HELD].values[prefix][key] += value
          }
        }
      })
    } else {
      acc[buildContributorKey(contributor)] = {
        label: getContributorLabel(contributor),
        notHeld: contributor.notHeld || false,
        values: {
          [prefix]: getFactorSensitivityValues(contributor.factors, selectedMetricDisplay),
        },
      }
    }
    return acc
  }, {})
}

export function parseGroupedContributors(prefix, groups, options = {}) {
  if (!isArray(groups)) {
    return {}
  }

  return chain(groups)
    .filter(group => group && group.id)
    .keyBy("id")
    .mapValues(group => {
      return {
        label: group.name || group.id,
        values: {
          [prefix]: getFactorSensitivityValues(group.factors, options.selectedMetricDisplay),
        },
        children: parseContributors(prefix, group.contributors, options),
      }
    })
    .value()
}

export function createFactorSensitivityContributorsQuery({ prefix, modelId, positionSet, base, to, groupByOptions, factorSensitivityType, selectedCategory, ...options }) {
  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const sanitizedPositionSet = sanitizePositionSet(positionSet)
  const { batchId, on, shouldUseIntradayQuery } = options
  const additionalVariables = shouldUseIntradayQuery ? {
    batchId,
    on,
    base,
    includeNotHeld: !!base,
  } : {
    base,
    date: to,
    includeNotHeld: !!base,
  }

  return {
    ...generateGathererKeys("factorSensitivity.contributors", {
      prefix, modelId, positionSet, base, to,
      groupBy, groupById, tier,
      intraday: shouldUseIntradayQuery || undefined,
      batchId, on,
      category: selectedCategory,
      sensitivityType: factorSensitivityType,
    }),
    query: getQuery(groupBy && groupByFields, shouldUseIntradayQuery),
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      batchId: batchId,
      category: selectedCategory,
      ...groupByFields,
      ...additionalVariables,
      ...getFactorSensitivityQueryParams(factorSensitivityType),
    },
    select: (result) => {
      const factorSensitivity = result.data.model.simulation.factorSensitivity
      const contributors = groupBy
        ? parseGroupedContributors(prefix, result.data.model.simulation.groupedFactorSensitivityContributors, options)
        : parseContributors(prefix, result.data.model.simulation.factorSensitivityContributors, options)

      const total = {
        label: "Total",
        values: {
          [prefix]: options.selectedMetricDisplay !== "standalone" ? getFactorSensitivityValues(factorSensitivity[0]?.factors, options.selectedMetricDisplay) : null,
        },
        children: contributors,
      }

      return total
    },
  }
}
